import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

/**
 * Global date range context.
 *
 * Drives every dashboard / client / campaigns / leads page from a single
 * top-bar dropdown. Persists the user's choice to localStorage so the
 * range survives page reloads.
 *
 * Consumers:
 *   const { range, setRange, from, to, label } = useGlobalDate();
 *   api.get("/api/leads", { params: { date_range: range } })
 */

export const RANGE_OPTIONS = [
  { key: "today",      label: "Today" },
  { key: "yesterday",  label: "Yesterday" },
  { key: "last_7d",    label: "Last 7 days" },
  { key: "last_14d",   label: "Last 14 days" },
  { key: "last_21d",   label: "Last 21 days" },
  { key: "last_28d",   label: "Last 28 days" },
  { key: "last_30d",   label: "Last 30 days" },
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "last_3m",    label: "Last 3 months" },
  { key: "last_6m",    label: "Last 6 months" },
  { key: "last_1y",    label: "Last 1 year" },
  { key: "custom",     label: "Custom range…" },
];

const STORAGE_KEY = "adsintel.globalDateRange.v1";

function todayISO(offsetDays = 0) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

function startOfMonth(offsetMonths = 0) {
  const d = new Date();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + offsetMonths);
  return d.toISOString().slice(0, 10);
}

function endOfMonth(offsetMonths = 0) {
  const d = new Date();
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + offsetMonths + 1);
  d.setUTCDate(0);  // last day of previous month after the +1
  return d.toISOString().slice(0, 10);
}

/** Given a key, return the {from, to} ISO date strings. */
export function resolveRange(key, customFrom, customTo) {
  switch (key) {
    case "today":      return { from: todayISO(0),    to: todayISO(0) };
    case "yesterday":  return { from: todayISO(-1),   to: todayISO(-1) };
    case "last_7d":    return { from: todayISO(-6),   to: todayISO(0) };
    case "last_14d":   return { from: todayISO(-13),  to: todayISO(0) };
    case "last_21d":   return { from: todayISO(-20),  to: todayISO(0) };
    case "last_28d":   return { from: todayISO(-27),  to: todayISO(0) };
    case "last_30d":   return { from: todayISO(-29),  to: todayISO(0) };
    case "this_month": return { from: startOfMonth(0),  to: todayISO(0) };
    case "last_month": return { from: startOfMonth(-1), to: endOfMonth(-1) };
    case "last_3m":    return { from: todayISO(-89),    to: todayISO(0) };
    case "last_6m":    return { from: todayISO(-179),   to: todayISO(0) };
    case "last_1y":    return { from: todayISO(-364),   to: todayISO(0) };
    case "custom":     return { from: customFrom || todayISO(-29), to: customTo || todayISO(0) };
    default:           return { from: todayISO(-29), to: todayISO(0) };
  }
}

const Ctx = createContext({
  range: "last_30d",
  customFrom: "",
  customTo: "",
  setRange: () => {},
  setCustom: () => {},
  from: "",
  to: "",
  label: "",
});

export function GlobalDateProvider({ children }) {
  // Hydrate from localStorage so the range persists across reloads.
  const initial = (() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
      return {
        range: saved.range || "last_30d",
        customFrom: saved.customFrom || "",
        customTo: saved.customTo || "",
      };
    } catch {
      return { range: "last_30d", customFrom: "", customTo: "" };
    }
  })();

  const [range, setRange] = useState(initial.range);
  const [customFrom, setCustomFrom] = useState(initial.customFrom);
  const [customTo, setCustomTo] = useState(initial.customTo);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ range, customFrom, customTo }));
    } catch {
      // ignore quota / private-mode failures — UI still works without persistence
    }
  }, [range, customFrom, customTo]);

  const value = useMemo(() => {
    const { from, to } = resolveRange(range, customFrom, customTo);
    const label = RANGE_OPTIONS.find((r) => r.key === range)?.label || "Custom";
    return {
      range, customFrom, customTo, from, to, label,
      setRange,
      setCustom: (f, t) => { setCustomFrom(f); setCustomTo(t); },
    };
  }, [range, customFrom, customTo]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useGlobalDate() {
  return useContext(Ctx);
}
